/**
 * W11 — format the bottle/unit size of a PurchaseOrderLine for display.
 *
 * PurchaseOrderLine carries two parallel snapshot fields:
 *   - sizeValue + sizeUnit  (canonical; written for every new line)
 *   - bottleSize            (legacy; written by pre-migration documents)
 *
 * Imported inventory items populate ONLY sizeValue + sizeUnit — older
 * code that read `line.bottleSize` rendered blank for them. This helper
 * resolves the right field set:
 *
 *   1. sizeValue + sizeUnit  → e.g. "750 ml", "1.5 L", "12 unit"
 *   2. bottleSize            → e.g. "750ml", "1.5L"  (always treated as ml)
 *   3. ''                    → blank string (caller renders an em-dash)
 *
 * The two output formats differ slightly because the legacy field is
 * always millilitres while the canonical pair carries an explicit unit;
 * keeping the visual distinction makes it easy to spot in QA whether a
 * line is reading the new snapshot or falling back to the old one.
 */
export const formatPoLineSize = (
  line: { sizeValue?: number | null; sizeUnit?: string | null; bottleSize?: number | null } | null | undefined,
): string => {
  if (!line) return '';
  const { sizeValue, sizeUnit, bottleSize } = line;
  if (sizeValue != null) {
    const unit = (sizeUnit ?? 'ml').trim() || 'ml';
    return `${sizeValue} ${unit}`;
  }
  if (bottleSize != null && isFinite(bottleSize) && bottleSize > 0) {
    return bottleSize >= 1000 ? `${bottleSize / 1000}L` : `${bottleSize}ml`;
  }
  return '';
};
